import React, { useState, useEffect } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useCompanyId } from '../../hooks/useCompanyId';
import { useHRDashboard } from '../../hooks/useHRDashboard';
import HRMaterialCard from './HRMaterialCard';
import TailorTemplateModal from './TailorTemplateModal';
import DocumentViewerModal from '../DocumentViewerModal';
import { DEFAULT_HR_SECTIONS, decorateSections } from '../../utils/hrMaterials';
import { fetchHRStructure } from '../../services/hrStructureService';
import { findTailorableTemplate, findTemplateByTitle, getTemplateViewUrl } from '../../utils/hrTemplates';

const materialDomId = (title) => `hr-material-${String(title).replace(/[^a-zA-Z0-9]+/g, '-')}`;

const SectionHeading = ({ icon: Icon, emoji, children }) => {
  const { darkMode } = useTheme();
  return (
    <div className="flex items-center justify-center gap-3 mb-5">
      <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
      <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
        {emoji ? (
          <span className="text-xl leading-none">{emoji}</span>
        ) : (
          Icon && <Icon className="w-5 h-5 text-red-600 dark:text-red-400" />
        )}
      </span>
      <h2 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {children}
      </h2>
      <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
    </div>
  );
};

const HRMaterialsTab = ({ overrideCompanyId = null, overrideUserData = null }) => {
  const location = useLocation();
  const { darkMode } = useTheme();
  const { companyId } = useCompanyId();
  const { userData } = useOutletContext() || {};
  const { documents } = useHRDashboard();
  // In the CRM Portal View this tab is scoped to the selected client so template
  // tailoring uses the client's company/brand info, not the internal viewer's.
  const effectiveCompanyId = overrideCompanyId || companyId;
  const effectiveUserData = overrideUserData || userData;

  const [tailorItem, setTailorItem] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [materialSections, setMaterialSections] = useState(() => decorateSections(DEFAULT_HR_SECTIONS));
  const [highlightTitle, setHighlightTitle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');

  // Sidebar search deep link: /portal/hr/materials?material=<title> scrolls to the card.
  useEffect(() => {
    const material = new URLSearchParams(location.search).get('material');
    if (!material) return undefined;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(materialDomId(material));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightTitle(material);
      }
    }, 350);
    const clearTimer = setTimeout(() => setHighlightTitle(null), 3200);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [location.search, materialSections]);

  useEffect(() => {
    let cancelled = false;
    fetchHRStructure()
      .then((sections) => {
        if (!cancelled) setMaterialSections(decorateSections(sections));
      })
      .catch((err) => console.error('Error loading HR structure:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // View opens the file in a fullscreen in-page overlay instead of a new tab.
  const handleView = (item) => {
    const url = getTemplateViewUrl(findTemplateByTitle(documents, item.title));
    if (!url) {
      alert('This template is not available yet. Please check back later.');
      return;
    }
    setViewerDoc({ url, title: item.name || item.title });
  };

  // Sections with templates; search + group filter narrow them further.
  const populatedSections = materialSections.filter((section) => section.items.length > 0);
  const q = searchQuery.trim().toLowerCase();
  const visibleSections = populatedSections
    .filter((s) => groupFilter === 'all' || s.id === groupFilter)
    .map((s) => ({
      ...s,
      items: q
        ? s.items.filter(
            (i) =>
              (i.name || i.title).toLowerCase().includes(q) ||
              (i.description || '').toLowerCase().includes(q) ||
              s.label.toLowerCase().includes(q)
          )
        : s.items,
    }))
    .filter((s) => s.items.length > 0);

  const inputBase = `rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
    darkMode
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
  }`;

  return (
    <div>
      {/* Search + material group filter */}
      {populatedSections.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search materials..."
              className={`w-full pl-9 pr-3 py-2 ${inputBase}`}
            />
          </div>
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={`sm:w-56 px-3 py-2 ${inputBase}`}
          >
            <option value="all">All groups</option>
            {populatedSections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.emoji ? `${s.emoji} ` : ''}{s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {populatedSections.length > 0 && visibleSections.length === 0 && (
        <p className={`text-sm text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No materials match your search.
        </p>
      )}

      {/* Material sections (empty sections stay hidden until CRM adds templates) */}
      {visibleSections.map((section) => (
        <div key={section.id} className="mt-10 first:mt-0">
          <SectionHeading icon={section.icon} emoji={section.emoji}>{section.label}</SectionHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.items.map((item) => (
              <div
                key={item.title}
                id={materialDomId(item.title)}
                className={`rounded-xl transition-shadow ${
                  highlightTitle === item.title ? 'ring-2 ring-emerald-500 shadow-lg' : ''
                }`}
              >
                <HRMaterialCard item={item} documents={documents} onTailor={setTailorItem} onView={handleView} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <DocumentViewerModal
        isOpen={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        title={viewerDoc?.title}
      />

      <TailorTemplateModal
        isOpen={!!tailorItem}
        onClose={() => setTailorItem(null)}
        template={tailorItem}
        templateDoc={tailorItem ? findTailorableTemplate(documents, tailorItem.title) : null}
        companyId={effectiveCompanyId}
        userData={effectiveUserData}
      />
    </div>
  );
};

export default HRMaterialsTab;
